import AntIcon from "@/components/icons/AntIcon";
import { Alert, Button, Empty, Flex, Popconfirm, Skeleton, Tooltip } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDeleteCommissionAdvanceCancellation, useGetCommissionAdvance } from "../../../../common/api/queries";
import { Permission } from "../../../../common/security/enums";
import { useHasPermissions } from "../../../auth/hooks";
import { InvestmentFeeType } from "../../../contract/enums";
import type { InvestmentContract } from "../../../contract/types";
import { contractPermissionsByContractType } from "../../../contract/utils";
import { CommissionAdvance } from "../types";
import { isCommissionAdvanceInUnfinishedBatch, isCommissionAdvanceRunning } from "../utils";
import { CommissionAdvanceDetail } from "./CommissionAdvanceDetail";
import { CommissionAdvanceCancellationModalForm } from "./forms/CommissionAdvanceCancellationModalForm";
import { CommissionAdvanceModalForm } from "./forms/CommissionAdvanceModalForm";

interface ActionsProps {
  contract: InvestmentContract;
  advance: CommissionAdvance;
}

const CommissionAdvanceActions = ({ contract, advance }: ActionsProps) => {
  const { t } = useTranslation();

  const [isOpenAdvanceModal, setIsOpenAdvanceModal] = useState(false);
  const [isOpenCancellationModal, setIsOpenCancellationModal] = useState(false);

  const deleteCommissionAdvanceCancellation = useDeleteCommissionAdvanceCancellation();

  if (isCommissionAdvanceInUnfinishedBatch(advance)) {
    return (
      <Alert
        type="info"
        showIcon
        className="margin-bottom-medium"
        title={t("commissions.advance.helpers.inUnfinishedBatch")}
      />
    );
  }

  return (
    <>
      <Flex gap="small" className="margin-bottom-medium">
        <Button
          variant="outlined"
          color="primary"
          icon={<AntIcon type="edit" />}
          onClick={() => setIsOpenAdvanceModal(true)}
        >
          {t("commissions.advance.actions.update")}
        </Button>

        {advance.manualCancellationReason ? (
          <Popconfirm
            title={t("commissions.advance.helpers.deleteCancellationConfirm")}
            cancelText={t("common.no")}
            okText={t("common.yes")}
            onConfirm={() => deleteCommissionAdvanceCancellation.mutate({ id: contract.id })}
          >
            <Button icon={<AntIcon type="rollback" />}>{t("commissions.advance.actions.deleteCancellation")}</Button>
          </Popconfirm>
        ) : (
          <Button danger icon={<AntIcon type="stop" />} onClick={() => setIsOpenCancellationModal(true)}>
            {t("commissions.advance.actions.createCancellation")}
          </Button>
        )}
      </Flex>

      <CommissionAdvanceModalForm
        contract={contract}
        advance={advance}
        isOpen={isOpenAdvanceModal}
        onCancel={() => setIsOpenAdvanceModal(false)}
      />

      <CommissionAdvanceCancellationModalForm
        contractId={contract.id}
        advance={advance}
        isOpen={isOpenCancellationModal}
        onCancel={() => setIsOpenCancellationModal(false)}
      />
    </>
  );
};

const getCreateDisabledReasonKey = (contract: InvestmentContract) => {
  if (contract.feeType !== InvestmentFeeType.PREPAID) {
    return "commissions.advance.helpers.createOnlyForPrepaidFee";
  }

  if (!(contract.feeTotalAmount > 0 && contract.feePaymentsCount > 0)) {
    return "commissions.advance.helpers.createRequiresFeeParameters";
  }

  return undefined;
};

interface Props {
  contract: InvestmentContract;
}

export const ContractCommissionAdvance = ({ contract }: Props) => {
  const { t } = useTranslation();

  const [isOpenCreateModal, setIsOpenCreateModal] = useState(false);

  const { data: advance, isPending } = useGetCommissionAdvance(contract.id);

  const hasCommissionAdvanceManagePermission = useHasPermissions(
    Permission.COMMISSIONS_MANAGE,
    contractPermissionsByContractType[contract.type].update,
  );

  const createDisabledReasonKey = getCreateDisabledReasonKey(contract);

  return (
    <Skeleton active loading={isPending}>
      {advance ? (
        <>
          {hasCommissionAdvanceManagePermission && isCommissionAdvanceRunning(advance) && (
            <CommissionAdvanceActions contract={contract} advance={advance} />
          )}

          <CommissionAdvanceDetail advance={advance} />
        </>
      ) : (
        <>
          <Empty description={t("commissions.advance.helpers.noCommissionAdvance")}>
            {hasCommissionAdvanceManagePermission && (
              <Tooltip title={createDisabledReasonKey ? t(createDisabledReasonKey) : undefined}>
                <Button
                  type="primary"
                  icon={<AntIcon type="plus" />}
                  disabled={!!createDisabledReasonKey}
                  onClick={() => setIsOpenCreateModal(true)}
                >
                  {t("commissions.advance.actions.create")}
                </Button>
              </Tooltip>
            )}
          </Empty>

          <CommissionAdvanceModalForm
            contract={contract}
            isOpen={isOpenCreateModal}
            onCancel={() => setIsOpenCreateModal(false)}
          />
        </>
      )}
    </Skeleton>
  );
};
